import { ICache, MemoryCache } from '@ifit/in-a-cache';
import axios from 'axios';

export interface IStatusResponse {
  [key: string]: boolean;
}

export interface IStatusService {
  isDown(name: string): Promise<boolean>;
}

export interface IEnableStatusChecksArg {
  enableStatusChecks: boolean;
}

export interface IStatusArgs {
  statusEndpoint?: string,
  cache?: ICache,
  errorLogger?: IErrorLogger,
}

export interface IErrorLogger {
  error: (...msg: (string | Error)[]) => void;
}

const twoMinutesInSeconds = 120;
const oneSecondInMilliseconds = 1000;
const refreshAfterMilliseconds = twoMinutesInSeconds * oneSecondInMilliseconds;

export class StatusService implements IStatusService {
  private logPrefix = '@ifit/this-is-fine.status endpoint';
  constructor(
    private args: IStatusArgs = {
      statusEndpoint: process.env.STATUS_ENDPOINT,
      cache: new MemoryCache(),
      errorLogger: null,
    },
  ) {}
  private get configured() {
    return !!this.args.statusEndpoint;
  }

  public async isDown(name: string): Promise<boolean> {
    const safeName = this.toSafeName(name);
    const status = await this.getStatus();
    for (const statusName in status) {
      if (status.hasOwnProperty(statusName)
        && status[statusName]
        && safeName === statusName) {
        return true;
      }
    }
    return false;
  }

  private async getStatus(): Promise<IStatusResponse> {
    if (!this.args.cache) {
      return this.fetchStatus();
    }
    return this.args.cache.read<IStatusResponse>({
      bucket: 'Status',
      id: 'All',
      retrieve: () => this.fetchStatus(),
      refreshAfterMilliseconds,
    });
  }

  private async fetchStatus(): Promise<IStatusResponse> {
    let promise;
    if (!this.configured) {
      this.args.errorLogger?.error(`${this.logPrefix} not specified, pretending all is well!`);
      promise = Promise.resolve({ data: {} });
    } else {
      promise = axios
        .get(this.args.statusEndpoint, { timeout: 1000 })
        .catch(err => {
          this.args.errorLogger?.error?.(
            `${this.logPrefix} errored, squelching and pretending all is well!`,
            err,
          );
          return { data: {} };
        });
    }
    const { data: statusMap } = await promise;
    return Object.keys(statusMap).reduce(
      (status, key) => ({
        ...status,
        [this.toSafeName(key)]: statusMap[key],
      }),
      {},
    );
  }

  private toSafeName(val: string): string {
    return val.replace(/[^a-z0-9]/ig, '').toLowerCase();
  }
}
